import { z } from 'zod';
import { supabase } from './supabase';

const envSchema = z.object({
  AWS_REGION: z.string().min(1).default('us-east-1'),
  AWS_ACCESS_KEY_ID: z.string().min(16).max(128),
  AWS_SECRET_ACCESS_KEY: z.string().min(30),
  AWS_S3_BUCKET: z.string().min(3).max(63),
  OPENAI_API_KEY: z.string().min(1),
  SUPABASE_URL: z.string().min(1),
  SUPABASE_ANON_KEY: z.string().min(1)
});

export async function validateConfig() {
  try {
    const { data: envConfig, error } = await supabase
      .from('env_config')
      .select('*')
      .eq('id', 1)
      .single();

    if (error) {
      throw new Error(`Failed to fetch environment configuration: ${error.message}`);
    }

    if (!envConfig) {
      throw new Error('Environment configuration not found. Please set up your configuration in the admin panel.');
    }

    const trimmedConfig = {
      AWS_REGION: envConfig.aws_region?.trim() || 'us-east-1',
      AWS_ACCESS_KEY_ID: envConfig.aws_access_key_id?.trim() || '',
      AWS_SECRET_ACCESS_KEY: envConfig.aws_secret_access_key?.trim() || '',
      AWS_S3_BUCKET: envConfig.aws_s3_bucket?.trim() || '',
      OPENAI_API_KEY: envConfig.openai_api_key?.trim() || '',
      SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
      SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY
    };

    if (!trimmedConfig.AWS_ACCESS_KEY_ID) {
      throw new Error('AWS Access Key ID is missing. Please check your AWS credentials in the admin panel.');
    }

    if (!trimmedConfig.AWS_SECRET_ACCESS_KEY) {
      throw new Error('AWS Secret Access Key is missing. Please check your AWS credentials in the admin panel.');
    }

    if (!trimmedConfig.AWS_S3_BUCKET) {
      throw new Error('AWS S3 bucket name is missing. Please configure your S3 bucket in the admin panel.');
    }

    if (!/^[a-z0-9][a-z0-9.-]*[a-z0-9]$/.test(trimmedConfig.AWS_S3_BUCKET)) {
      throw new Error('Invalid S3 bucket name format. Bucket names must contain only lowercase letters, numbers, hyphens, and periods.');
    }

    if (!trimmedConfig.OPENAI_API_KEY.startsWith('sk-')) {
      throw new Error('Invalid OpenAI API key format. The key should start with "sk-".');
    }

    try {
      const validatedConfig = envSchema.parse(trimmedConfig);
      return validatedConfig;
    } catch (validationError) {
      if (validationError instanceof z.ZodError) {
        const issues = validationError.errors.map(err => {
          switch (err.path[0]) {
            case 'AWS_ACCESS_KEY_ID':
              return 'Invalid AWS Access Key ID format';
            case 'AWS_SECRET_ACCESS_KEY':
              return 'Invalid AWS Secret Access Key format';
            case 'AWS_S3_BUCKET':
              return 'Invalid S3 bucket name';
            case 'OPENAI_API_KEY':
              return 'Invalid OpenAI API key';
            default:
              return `Invalid ${err.path[0]}`;
          }
        }).join(', ');
        throw new Error(`Configuration validation failed: ${issues}`);
      }
      throw validationError;
    }
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to validate environment configuration. Please check your settings in the admin panel.');
  }
}